import axios from 'axios';
import {getClientId} from 'common/src/modules/auth/auth.service';
import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import updateTemplateAssets from 'common/src/modules/creativeTemplates/store/creativeTemplatePage/movieAssets/actions/updateTemplateAssets';
import {TemplateAsset} from 'common/src/modules/dynamicParameters/dynamicParameters.types';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import aiImagesApi from '../../../../../aiImages/aiImages.api';
import {AssetMetadata, creativeTemplatesApi} from '../../../../creativeTemplates.api';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';

// Schema for updating template assets metadata
const inputSchema = z.object({
  assetIds: z.array(z.string()).describe('Array of asset IDs to update metadata for'),
});

function updateTemplateAssetsMetadataTool(store: CLStore) {
  return tool({
    name: AdBuilderAgentToolName.UpdateTemplateAssetsMetadata,
    displayName: 'Updating template assets metadata',
    description:
      'Updates metadata for specified template assets using AI image analysis to generate descriptions.',
    inputSchema,
    execute: async ({input}: ToolExecuteParams<typeof inputSchema>) => {
      try {
        const activeComp = getActiveComp(store);

        if (!activeComp) {
          return {
            content: 'ERROR: No active composition found',
          };
        }

        const state = store.getState();
        const templateId = state.creativeTemplatePage.template?.id;

        if (!templateId) {
          return {
            content: 'ERROR: No template ID found in store',
          };
        }

        const templateAssets = state.creativeTemplatePage.movieAssets.templateAssets;
        const clientId = String(getClientId());

        // Find the assets that match the provided IDs
        const assetsToUpdate = templateAssets.filter((asset) => input.assetIds.includes(asset.id));

        if (assetsToUpdate.length === 0) {
          return {
            content: `ERROR: No assets found with the provided IDs: ${input.assetIds.join(', ')}`,
          };
        }

        // Create a cancel token for the AI analysis requests
        const cancelTokenSource = axios.CancelToken.source();

        // Process each asset through AI analysis and update metadata
        const updatePromises = assetsToUpdate.map(async (asset) => {
          try {
            // Get AI analysis for the asset
            const assetMetadata = await aiImagesApi.analyzeImageFromUrl({
              imageUrl: asset.metadata.url,
              vendorService: 10, // Using OpenAiGpt4oMiniImageAnalysis
              cancelToken: cancelTokenSource.token,
            });

            // Extract description from the analysis
            const description = assetMetadata.identificationAndDescription.description;

            // Define the metadata to update
            const customMetadata: AssetMetadata = {
              ContentDescription: description,
              Source: 'uploaded by user',
            };

            // Update metadata via API
            await creativeTemplatesApi.updateAssetMetadata(
              clientId,
              templateId,
              asset.id,
              asset.metadata.type,
              customMetadata,
            );

            return {assetId: asset.id, success: true, description};
          } catch (error) {
            console.error(`Failed to process asset ${asset.id}:`, error);

            return {assetId: asset.id, success: false, error};
          }
        });

        // Wait for all API calls to complete
        const results = await Promise.all(updatePromises);

        // Update local state for successful updates
        store.dispatch(
          updateTemplateAssets((oldAssets: TemplateAsset[]) =>
            oldAssets.map((asset: TemplateAsset) => {
              const result = results.find((r) => r.assetId === asset.id);

              if (result?.success) {
                return {
                  ...asset,
                  metadata: {
                    ...asset.metadata,
                    customMetadata: {
                      ...asset.metadata.customMetadata,
                      ContentDescription: result.description,
                      Source: 'uploaded by user',
                    },
                  },
                };
              }

              return asset;
            }),
          ),
        );

        // Prepare response message
        const successfulUpdates = results.filter((r) => r.success).length;
        const failedUpdates = results.filter((r) => !r.success).length;

        return {
          content: `Successfully updated ${successfulUpdates} assets. ${failedUpdates} assets failed to update.`,
        };
      } catch (error) {
        return {
          content: `ERROR: ${error instanceof Error ? error.message : 'Failed to update template assets metadata'}`,
        };
      }
    },
  });
}

export default updateTemplateAssetsMetadataTool;
